package com.partejar.app.feature.contratos.presentation

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.partejar.app.core.ui.components.*
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.contratos.data.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import org.koin.compose.koinInject

sealed interface PublicSignatureUiState { data object Loading : PublicSignatureUiState; data class Content(val contract: PublicContract, val pdf: ContractPdf? = null, val submitting: Boolean = false, val error: String? = null, val completed: Boolean = false) : PublicSignatureUiState; data class Unavailable(val message: String) : PublicSignatureUiState }
class PublicContractSignatureViewModel(private val source: ContratosDataSource) : ViewModel() {
    private val _state = MutableStateFlow<PublicSignatureUiState>(PublicSignatureUiState.Loading); val state = _state.asStateFlow()
    fun load(token: String) = viewModelScope.launch { _state.value = try { PublicSignatureUiState.Content(source.publicContract(token)) } catch (error: CancellationException) { throw error } catch (_: Throwable) { PublicSignatureUiState.Unavailable("Este link é inválido, expirou, foi usado ou foi cancelado.") } }
    fun sign(token: String, strokes: String) = viewModelScope.launch { val current = _state.value as? PublicSignatureUiState.Content ?: return@launch; _state.value = current.copy(submitting = true, error = null); try { _state.value = current.copy(contract = source.signPublic(token, current.contract.documentHash, strokes), completed = true) } catch (error: CancellationException) { throw error } catch (_: Throwable) { _state.value = current.copy(error = "Não foi possível registrar a assinatura. Confira o link e tente novamente.") } }
    fun openPdf(token: String) = viewModelScope.launch { val current = _state.value as? PublicSignatureUiState.Content ?: return@launch; _state.value = current.copy(submitting = true, error = null); try { _state.value = current.copy(pdf = source.publicPdf(token)) } catch (error: CancellationException) { throw error } catch (_: Throwable) { _state.value = current.copy(error = "Não foi possível abrir o PDF do contrato.") } }
    fun pdfHandled(error: String?) {
        val current = _state.value as? PublicSignatureUiState.Content ?: return
        _state.value = current.copy(pdf = null, error = error)
    }
}

@Composable
fun PublicContractSignatureScreen(token: String, viewModel: PublicContractSignatureViewModel = koinInject()) {
    val state by viewModel.state.collectAsState(); val pdfHandler = rememberContractPdfHandler(); LaunchedEffect(token) { viewModel.load(token) }
    when (val current = state) {
        PublicSignatureUiState.Loading -> PublicSignatureLoading()
        is PublicSignatureUiState.Unavailable -> Box(Modifier.fillMaxSize().padding(PartejarTheme.spacing.space24), contentAlignment = androidx.compose.ui.Alignment.Center) { PartejarAlert("Link indisponível", PartejarAlertTone.ERROR) { Text(current.message) } }
        is PublicSignatureUiState.Content -> { current.pdf?.let { pdf -> LaunchedEffect(pdf) { viewModel.pdfHandled(openContractPdf(pdfHandler, pdf)) } }; PublicSignatureContent(current, onPdf = { viewModel.openPdf(token) }, onSign = { viewModel.sign(token, it) }) }
    }
}

@Composable private fun PublicSignatureLoading() {
    val spacing = PartejarTheme.spacing
    Column(
        Modifier.fillMaxSize().padding(spacing.space16),
        verticalArrangement = Arrangement.spacedBy(spacing.space12)
    ) {
        PartejarSkeletonText(Modifier.fillMaxWidth(0.42f).height(spacing.space12))
        PartejarSkeletonText(Modifier.fillMaxWidth(0.72f).height(spacing.space20))
        PartejarCard(Modifier.fillMaxWidth()) {
            Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                repeat(5) { PartejarSkeletonText(Modifier.fillMaxWidth().height(spacing.space12)) }
                PartejarSkeletonText(Modifier.fillMaxWidth(0.64f).height(spacing.space12))
            }
        }
        PartejarSkeleton(Modifier.fillMaxWidth().height(spacing.space64))
    }
}

@Composable private fun PublicSignatureContent(state: PublicSignatureUiState.Content, onPdf: () -> Unit, onSign: (String) -> Unit) {
    val spacing = PartejarTheme.spacing; var accepted by remember { mutableStateOf(false) }; var strokes by remember { mutableStateOf(emptyList<List<SignaturePoint>>()) }
    Column(
        Modifier
            .fillMaxSize()
            .imePadding()
            .verticalScroll(rememberScrollState())
            .padding(spacing.space16)
            .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom)),
        verticalArrangement = Arrangement.spacedBy(spacing.space12)
    ) {
        Text(state.contract.workspaceName, style = MaterialTheme.typography.labelLarge); Text("Contrato de ${state.contract.patientName}", style = MaterialTheme.typography.titleLarge)
        PartejarButton("Visualizar contrato", onPdf, variant = PartejarButtonVariant.SECONDARY, enabled = !state.submitting)
        if (state.completed) { PartejarAlert("Assinatura registrada", PartejarAlertTone.SUCCESS) { Text("A equipe será avisada para concluir as assinaturas internas.") }; return@Column }
        PartejarCard { Text(state.contract.content, Modifier.padding(spacing.space16)) }
        Text("Assinatura", style = MaterialTheme.typography.titleMedium)
        ContractSignaturePad(strokes, onChange = { strokes = it })
        PartejarButton("Limpar assinatura", { strokes = emptyList() }, variant = PartejarButtonVariant.SECONDARY, enabled = strokes.isNotEmpty())
        Row(verticalAlignment = androidx.compose.ui.Alignment.CenterVertically) { Checkbox(accepted, onCheckedChange = { accepted = it }); Text("Li o contrato e aceito assinar eletronicamente.") }
        state.error?.let { PartejarAlert("Não foi possível assinar", PartejarAlertTone.ERROR) { Text(it) } }
        val canSubmit = canSubmitSignature(accepted, strokes, state.submitting)
        PartejarButton(
            "Assinar contrato",
            { if (canSubmit) onSign(serializeSignatureStrokes(strokes)) },
            enabled = canSubmit,
            isLoading = state.submitting
        )
    }
}
